import {
  AfterContentInit,
  ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output,
  SimpleChanges
} from '@angular/core';
import { DeepLinker, NavController, NavParams } from "ionic-angular";
import { ApiService } from "../../../services/api.service";
import { ApiCollectionResponse, ApiViewData, Post, Category, UserGroup, ViewDidEnter, Tag } from '../../../app/app.interfaces';
import { AuthService } from "../../../services/auth.service";
import { SubComponent } from "../../../decorators/subcomponent";
import { UserGroupService } from "../../../services/user-group.service";
import { EventService } from "../../../services/event.service";
import { Subscription } from "rxjs/Subscription";
import { NavigationHelper } from "../../../helpers/navigation.helper";
import { Dashboard } from "../../../components/dashboard/dashboard";
import { IonicHelper } from "../../../helpers/ionic.helper";
import { CategoryService } from '../../../services/category.service';
import { NavSegment } from 'ionic-angular/umd/navigation/nav-util';
import { TagService } from "../../../services/tag.service";

@Component({
  selector: 'post-list-container',
  templateUrl: 'post-list-container.html'
})
@SubComponent()
export class PostListContainer implements OnChanges, OnDestroy, AfterContentInit {

  posts: Post[] = [];
  _view: ApiViewData = {
    loading: true
  };

	hideSpinner: boolean = false;

  set view(view: ApiViewData){
    this._view = view;
    this.viewChange.emit(view);
  }
  get view(){
    return this._view;
  }

	@Input() filters: any = null;
	@Input() noPostsText: any = 'POST.NO_POSTS';
	@Output() viewChange: EventEmitter<ApiViewData> = new EventEmitter<ApiViewData>();

  public currentPath: string = null;
  public currentSegment: any = null;

  private hasError: boolean = false;

  private reloadPostsSubscription: Subscription;

  private loadingIntervalId: any = null;

  private waitForSegmentsTimeout: any;

  constructor(private nav: NavController,
    private navParams: NavParams,
    private authService: AuthService,
    private apiService: ApiService,
    private userGroupService: UserGroupService,
    private categoryService: CategoryService,
    private tagService: TagService,
    private deepLinker: DeepLinker,
    private navigationHelper: NavigationHelper,
    private eventService: EventService,
    private ionic: IonicHelper,
    private changeDetector: ChangeDetectorRef) {

  }


  ngOnInit(): void {
    this.reloadPostsSubscription = this.eventService.reloadPosts.subscribe(() => {
      this.loadPosts(false, true);
    });


    this.loadingIntervalId = setInterval(()=>{
      if(this.apiService.isConnected) this.loadPosts(false, true);
    },60000);//Reload posts every minute

    // this.loadPosts(false);
  }


  ngOnChanges(changes: SimpleChanges): void {
    if(changes.filters && this.filters){
      this.loadPosts(false);
    }
  }

  ngOnDestroy(): void {
    if(this.reloadPostsSubscription) this.reloadPostsSubscription.unsubscribe();
    if(this.loadingIntervalId) clearInterval(this.loadingIntervalId);
  }

  ngAfterContentInit(): void {
    let currentSegments = this.deepLinker.getCurrentSegments();
    this.loadFiltersFromSegment(currentSegments);
  }

  private loadFiltersFromSegment(segments: NavSegment[]): void {

    let dashboard: Dashboard = null;
    if(this.navigationHelper.getRootNav() && this.navigationHelper.getRootNav().getViews().length
      && this.navigationHelper.getRootNav().getViews()[0].instance && this.navigationHelper.getRootNav().getViews()[0].instance instanceof Dashboard) {
      dashboard = this.navigationHelper.getRootNav().getViews()[0].instance;
    }

    let filterSegment = segments.find(segment => segment.component.name === this.constructor.name);
    this.currentPath = this.navigationHelper.currentPath;

    if(filterSegment!==undefined) {

      this.currentSegment = filterSegment;

      if(dashboard) dashboard.propagatePageTitle(filterSegment.name);

      this.userGroupService.unsetActiveUserGroup();

      let parseFilterSegmentPromise = new Promise((resolve, reject)=> {

        switch (filterSegment.name) {

          case "post-list-most-recent":
            resolve();
            break;
          case "post-list-my-posts":
            resolve({user: this.authService.user.id});
            break;
          case "post-list-answered":
            resolve({hasReactions: 1});
            break;
          case "post-list-unanswered":
            resolve({hasReactions: 0});
            break;
          case "post-list-favourites":
            resolve({userFavourite: this.authService.user.id});
            break;
          case "post-list-group-posts":

            let activeUserGroupId = parseInt(this.navParams.get('userGroupId'));

            this.userGroupService.setActiveUserGroupById(activeUserGroupId).then((activeUserGroup: UserGroup) => {
              if(dashboard) dashboard.propagatePageTitle(filterSegment.name, activeUserGroup);
              resolve({userGroups: activeUserGroupId});
            }).catch((err)=>{
              reject('GROUP_NOT_FOUND');
            });

            break;
          case "post-list-category":

            let activeCategoryId = parseInt(this.navParams.get('categoryId'));

            this.categoryService.setActiveCategoryById(activeCategoryId).then((activeCategory: Category) => {
              if(dashboard) dashboard.propagatePageTitle(filterSegment.name, activeCategory);
              resolve({categories: activeCategoryId});
            }).catch((err)=>{
              reject('CATEGORY_NOT_FOUND');
            });

            break;
          case "post-list-tag":
            let tagId = this.navParams.get('tagId');
            if(dashboard) {
              this.tagService.findByIdAsync(parseInt(tagId, 10)).then((tag)=> {
                dashboard.propagatePageTitle(filterSegment.name, tag);
              });
            }
            resolve({tags: [tagId]});
            break;
          default:
            // console.log(filterSegment.name);
            //reject('PAGE_NOT_FOUND');
            break;
        }

      });

      parseFilterSegmentPromise.then((filters: any) => {
        this.filters = filters;
        this.loadPosts();
      }).catch((err) => {
        console.error(err);
        this.hasError = true;
        this.nav.setRoot('post-list-most-recent');

      });


    }
    else {
      setTimeout(() => {
        this.navigationHelper.forcePathChangeUpdate();//Fix buttons being not active on first load due to timing issues
      },1000);
    }

  }



  loadMore(): void {
    this.loadPosts(true, false, this.view.pages.next);
  }

  loadPosts(append: boolean = true, prepend: boolean = false, overrideUri: string = null): void {

    if(this.hasError) return;

    this.view.loading = true;
    let uri = 'posts';

    if(this.filters !== null && this.filters !== undefined && this.filters.userFavourite !== undefined){
      uri = 'users/'+this.filters.userFavourite+'/favourite_posts';
    }

    if(overrideUri) uri = overrideUri;


    this.apiService.getCollection(uri, this.filters).then(
      (apiResponse: ApiCollectionResponse) => {

        let triggerChangeDetection: boolean = false;

        if(append) {
          for(let item of apiResponse.items) {
            //Check if existing
            if(!this.posts.find(post => post.id === item.id)) {
              this.posts.push(item);
              triggerChangeDetection = true;
            }
          }
        }
        else if(prepend){
          for(let item of apiResponse.items.reverse()){
            //Check if existing
            let existingPost: Post = this.posts.find(post => post.id === item.id);
            if(existingPost) {
              //update num answers
              if(existingPost.reactionsCount !== item.reactionsCount){
                existingPost.reactionsCount = item.reactionsCount;
                triggerChangeDetection = true;
              }
            }
            else {
              this.posts.unshift(item);
              triggerChangeDetection = true;
            }
          }
        }
        else {
          this.posts = apiResponse.items;
          triggerChangeDetection = true;
        }
        this.view = Object.assign({}, apiResponse.view, { loading: false });

        if(triggerChangeDetection){
          this.changeDetector.detectChanges();
        }

    }).catch((err) => {
      console.error(err);
      this.view.loading = false;
      //this.ionic.alert('GLOBAL.GENERAL_ERROR');
    });


  }

}



// WEBPACK FOOTER //
// ./src/partials/post/post-list-container/post-list-container.ts